import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Play, ExternalLink } from "lucide-react";

const CAST = [
  "Sonya Kraus", "Marvin Holm", "Anselm Roser", "Mario Novembre",
  "Chris Irslinger", "Nathan Goldblat", "Leon Pelz", "Luana Knoell",
  "Chany Dakota", "Lisa Kueppers", "Payton.R", "Nika Sofie",
];

const STATS = [
  { value: "4 STAFFELN", label: "Mit 72 Folgen" },
  { value: "CAST", label: "Schauspieler wie TV-Star Sonya Kraus treffen auf Creator" },
  { value: "20 MIO+", label: "Kombinierte Cast-Reichweite" },
  { value: "GRATIS", label: "Streambar auf Joyn" },
];

const FACTS = [
  { label: "FORMAT", value: "Streaming Series" },
  { label: "PLATTFORM", value: "Joyn Original" },
  { label: "PRODUKTION", value: "Pantaflix Studios" },
  { label: "VERLÄNGERUNG", value: "Social Media" },
];

export default function DasInternat() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const startTrailer = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    v.volume = 1;
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
    setPlaying(true);
  };

  return (
    <section className="relative min-h-screen overflow-hidden pb-24 pt-32 md:pt-40">
      <div className="mx-auto max-w-[1200px] px-5 md:px-10">
        <Link to="/" data-testid="case-back-link" className="mono-label inline-flex items-center gap-2 text-xs text-white/60 transition-colors hover:text-[var(--crtr-yellow)]">
          <ArrowLeft className="h-4 w-4" /> ZURÜCK
        </Link>

        <p className="mono-label mt-8 text-xs text-[var(--crtr-yellow)]">CASE 02 — FICTIONAL SERIES))</p>
        <h1 className="font-display mt-4 text-5xl text-white sm:text-6xl lg:text-7xl">DAS INTERNAT.</h1>
        <p className="mono-label mt-4 max-w-2xl text-sm text-white/70">
          FICTIONAL CONTENT PRODUCTION MIT CREATOR. EIN JOYN ORIGINAL.
        </p>

        {/* trailer player */}
        <div className="relative mt-12 overflow-hidden rounded-[2rem] bg-black shadow-[0_30px_80px_rgba(0,0,0,0.5)]" data-testid="internat-trailer-player">
          <video
            ref={videoRef}
            src="/das-internat-trailer.mp4"
            poster="/das-internat-still.jpg"
            playsInline
            controls={playing}
            onEnded={() => setPlaying(false)}
            className="aspect-video w-full object-cover"
          />
          {!playing && (
            <button
              onClick={startTrailer}
              data-testid="internat-trailer-play"
              aria-label="Trailer mit Ton abspielen"
              className="group absolute inset-0 flex items-center justify-center bg-black/30 transition-colors hover:bg-black/20"
            >
              <span className="flex flex-col items-center gap-4">
                <span className="flex h-20 w-20 items-center justify-center rounded-full bg-[var(--crtr-yellow)] transition-transform group-hover:scale-110">
                  <Play className="ml-1 h-8 w-8 text-black" fill="currentColor" />
                </span>
                <span className="mono-label text-xs text-white">TRAILER MIT TON ABSPIELEN</span>
              </span>
            </button>
          )}
        </div>

        {/* facts */}
        <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4">
          {FACTS.map((f) => (
            <div key={f.label} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <p className="mono-label text-[10px] text-white/50">{f.label}</p>
              <p className="mt-2 font-heavy text-lg uppercase text-white">{f.value}</p>
            </div>
          ))}
        </div>

        {/* stats */}
        <div className="mt-14 grid grid-cols-2 gap-x-6 gap-y-10 border-y border-white/10 py-10 md:grid-cols-4" data-testid="internat-stats">
          {STATS.map((s) => (
            <div key={s.label}>
              <p className="font-display text-3xl text-[var(--crtr-yellow)] md:text-4xl">{s.value}</p>
              <p className="mono-label mt-2 text-[10px] text-white/60">{s.label}</p>
            </div>
          ))}
        </div>

        {/* story + poster */}
        <div className="mt-16 grid gap-10 md:grid-cols-[1.4fr_1fr] md:items-start">
          <div>
            <h2 className="font-display text-3xl text-white md:text-4xl">CREATOR. DRAMA. INTERNAT.</h2>
            <div className="mt-6 max-w-xl space-y-4 text-base leading-relaxed text-white/80">
              <p>
                Mit „Das Internat“ zeigen wir, wie Fictional Content Production mit Creator funktioniert:
                Deutschlands größte Social-Media-Talente spielen die Hauptrollen einer eigens entwickelten
                Serie — produziert für junge Zielgruppen, erzählt in ihrer Sprache, distribuiert dort,
                wo sie zuhause sind.
              </p>
              <p>
                Ein Joyn Original, produziert mit Pantaflix Studios: Creator-Reichweite
                trifft professionelle Serienproduktion. Das Ergebnis — echte Relevanz statt gekaufter
                Aufmerksamkeit, kostenlos streambar auf Joyn.
              </p>
            </div>

            <h3 className="mono-label mt-10 text-xs text-[var(--crtr-yellow)]">CAST))</h3>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="internat-cast-list">
              {CAST.map((n) => (
                <span key={n} className="mono-label rounded-full border border-white/15 px-4 py-1.5 text-xs text-white/80">
                  {n}
                </span>
              ))}
            </div>

            <a
              href="https://www.joyn.de/serien/das-internat"
              target="_blank"
              rel="noreferrer"
              data-testid="internat-joyn-link"
              className="group mt-10 inline-flex items-center gap-3 rounded-full bg-[var(--crtr-yellow)] py-3 pl-6 pr-3"
            >
              <span className="mono-label text-xs text-black">JETZT KOSTENLOS AUF JOYN STREAMEN</span>
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black">
                <ExternalLink className="h-4 w-4 text-[var(--crtr-yellow)] transition-transform group-hover:translate-x-0.5" />
              </span>
            </a>
          </div>

          <div className="overflow-hidden rounded-[2rem]">
            <img loading="lazy" src="/das-internat-poster.jpg" alt="Das Internat – Plakat" data-testid="internat-poster" className="w-full" />
          </div>
        </div>
      </div>
    </section>
  );
}
